import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import PasswordStrengthIndicator from '../ui/forms/PasswordStrengthIndicator';
import { ArcMark } from '../ui/ArcMark';
import { handleValidationError, formatErrorMessage } from '../../utils/errorHandler';
import Modal from '../ui/Modal';
import TermsModalContent from '../legal/TermsModalContent';
import PrivacyPolicyModalContent from '../legal/PrivacyPolicyModalContent';

const Register: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  const { signUp, error: authError, loading, clearError } = useAuth();
  const navigate = useNavigate();

  const handleNavigateToLogin = () => {
    clearError();
    setValidationError(null);
    navigate('/');
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      const errorState = handleValidationError('emailRequired');
      return setValidationError(formatErrorMessage(errorState));
    }

    if (password !== confirmPassword) {
      const errorState = handleValidationError('passwordsNoMatch');
      return setValidationError(formatErrorMessage(errorState));
    }

    if (password.length < 8) {
      const errorState = handleValidationError('passwordTooShort');
      return setValidationError(formatErrorMessage(errorState));
    }

    if (!acceptedTerms) {
      return setValidationError('You must accept the Terms and Conditions to continue.');
    }

    try {
      setValidationError(null);
      await signUp(email, password, "portal_user");
      navigate('/dashboard');
    } catch (err: any) {
      // Error is already set in AuthContext
    }
  };

  const passwordsMatch = confirmPassword.length > 0 && password === confirmPassword;
  const passwordsMismatch = confirmPassword.length > 0 && password !== confirmPassword;

  return (
    <div className="pk-auth">
      <div className="pk-auth-pane">
        <div className="pk-auth-card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <ArcMark variant="current" size={24} style={{ color: 'var(--arc-accent)' }} />
            <span style={{ fontFamily: 'var(--arc-font-display)', fontWeight: 800, fontSize: 18, color: 'var(--arc-ink)' }}>ARC Portal</span>
          </div>

          <div>
            <h1 className="pk-h2">Create your account</h1>
            <p className="pk-sub" style={{ marginTop: 4 }}>Get started with API keys, models, and usage tracking.</p>
          </div>

          {(validationError || authError) && (
            <div className="pk-note pk-note-danger">{validationError || authError}</div>
          )}

          <form onSubmit={handleRegister} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="pk-field">
              <label className="pk-field-label" htmlFor="email">Email Address</label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                autoFocus
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                className="pk-input"
              />
            </div>

            <div className="pk-field">
              <label className="pk-field-label" htmlFor="password">Password</label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                className="pk-input"
              />
              <PasswordStrengthIndicator password={password} />
            </div>

            <div className="pk-field">
              <label className="pk-field-label" htmlFor="confirm-password">Confirm Password</label>
              <input
                id="confirm-password"
                name="confirm-password"
                type="password"
                autoComplete="new-password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={loading}
                aria-invalid={passwordsMismatch}
                className="pk-input"
              />
              {passwordsMismatch && <p className="pk-error">Passwords do not match</p>}
              {passwordsMatch && <p className="pk-help" style={{ color: 'var(--arc-success)' }}>Passwords match</p>}
            </div>

            <label className="pk-check" style={{ alignItems: 'flex-start' }}>
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                disabled={loading}
                style={{ marginTop: 2 }}
              />
              <span className="pk-small" style={{ color: 'var(--arc-ink-2)' }}>
                I agree to the{' '}
                <button type="button" onClick={() => setShowTermsModal(true)} className="pk-btn-text" style={{ display: 'inline', padding: 0 }}>Terms of Use</button>
                {' '}and{' '}
                <button type="button" onClick={() => setShowPrivacyModal(true)} className="pk-btn-text" style={{ display: 'inline', padding: 0 }}>Privacy Policy</button>
              </span>
            </label>

            <button
              type="submit"
              disabled={!email || !password || !confirmPassword || password !== confirmPassword || !acceptedTerms || loading}
              className="pk-btn pk-btn-grad pk-btn-lg pk-btn-block"
            >
              {loading ? <span className="pk-spinner" style={{ borderTopColor: '#fff' }} /> : 'Sign Up'}
            </button>
          </form>

          <hr className="pk-rule" />

          <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <p className="pk-sub">Already have an account?</p>
            <button type="button" onClick={handleNavigateToLogin} className="pk-btn pk-btn-outline pk-btn-block">
              Sign In
            </button>
          </div>
        </div>
      </div>

      <div className="pk-auth-side">
        <span className="pk-label">ARC · Developer Portal</span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <h3 style={{ fontFamily: 'var(--arc-font-display)', fontWeight: 800, fontSize: 30, letterSpacing: '-.035em', lineHeight: 1.1, margin: 0, color: '#F7F5F3', maxWidth: '22ch' }}>
            One key, every model.
          </h3>
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.65, color: 'rgba(247,245,243,.66)', maxWidth: '44ch' }}>
            Manage access, usage, and routing rules in one place — task determination and per-call accounting behind a single endpoint.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
          <span className="pk-mono" style={{ fontSize: 11, letterSpacing: '.16em', textTransform: 'uppercase', color: 'rgba(247,245,243,.5)' }}>api.askarc.app</span>
          <span className="pk-mono" style={{ fontSize: 11, letterSpacing: '.16em', textTransform: 'uppercase', color: 'rgba(247,245,243,.5)' }}>docs</span>
        </div>
      </div>

      {/* Terms Modal */}
      <Modal isOpen={showTermsModal} onClose={() => setShowTermsModal(false)} size="lg">
        <TermsModalContent onClose={() => setShowTermsModal(false)} />
      </Modal>

      {/* Privacy Policy Modal */}
      <Modal isOpen={showPrivacyModal} onClose={() => setShowPrivacyModal(false)} size="lg">
        <PrivacyPolicyModalContent onClose={() => setShowPrivacyModal(false)} />
      </Modal>
    </div>
  );
};

export default Register;
